import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { validateCartSnapshots, type CartValidationIssue } from './cart/cartValidation'
import { clearWeeklyOrderProducts, setWeeklyOrderProducts } from './config/businessData'
import { fetchPublishedMenu } from './config/liveMenu'
import { clearPricingDelivery, fetchPricingDelivery, installPricingDelivery } from './config/livePricingDelivery'
import { migrateCartProductIdsForCatalogue, readCartBoxes } from './order/cartStorage'
import ClosedStoreState from './store/ClosedStoreState'
import { usePublicStoreStatus } from './store/usePublicStoreStatus'
import './styles/menu-authority-gate.css'

const CartPageV3 = lazy(() => import('./cart/CartPageV3'))
const CheckoutPageV3 = lazy(() => import('./checkout/CheckoutPageV3Enhanced'))

type Target = 'cart' | 'checkout'
type GateState = 'loading' | 'ready' | 'stale-cart' | 'menu-unavailable' | 'pricing-unavailable'

function staleCartMessage(issues: readonly CartValidationIssue[]) {
  const soldOut = issues.find((issue) => issue.code === 'product-sold-out')
  if (soldOut) {
    const name = soldOut.productName?.trim() || 'A flavour'
    return `${name} has sold out for this week. Please remove or replace it before checkout.`
  }
  if (issues.some((issue) => issue.code === 'pack-price-changed')) {
    return 'A pack price has changed since this cart was saved. Review or rebuild the affected box before checkout.'
  }
  if (issues.some((issue) => issue.code === 'product-unavailable')) {
    return 'One or more flavours in this cart are no longer on the published weekly menu. Edit or remove the affected box before checkout.'
  }
  if (issues.some((issue) => issue.code === 'pack-unavailable')) {
    return 'A saved pack is no longer available. Review the affected box before checkout.'
  }
  return 'Your saved order needs to be reviewed before checkout.'
}

function SavedCartAlert({ target, issues }: { target: Target; issues: readonly CartValidationIssue[] }) {
  return (
    <div className="menu-authority-alert" role="alert">
      <strong>Your saved order needs an update</strong>
      <span>{staleCartMessage(issues)}</span>
      {target === 'checkout'
        ? <a href="/cart">Review your cart</a>
        : <span>Edit or remove the affected box before continuing to checkout.</span>}
    </div>
  )
}

export default function MenuDependentRuntimePage({ target }: { target: Target }) {
  const [state, setState] = useState<GateState>('loading')
  const [issues, setIssues] = useState<CartValidationIssue[]>([])
  const storeStatus = usePublicStoreStatus()
  const query = new URLSearchParams(window.location.search)
  const allowHostedSuccessReturn = target === 'checkout'
    && query.get('stripe') === 'success'
    && Boolean(query.get('session_id')?.trim())

  const refreshAuthority = useCallback(async () => {
    if (storeStatus.state !== 'open' && !allowHostedSuccessReturn) return
    let menu: Awaited<ReturnType<typeof fetchPublishedMenu>>
    try {
      menu = await fetchPublishedMenu()
    } catch {
      clearWeeklyOrderProducts()
      setIssues([])
      setState('menu-unavailable')
      return
    }

    let pricing: Awaited<ReturnType<typeof fetchPricingDelivery>>
    try {
      pricing = await fetchPricingDelivery()
    } catch {
      clearPricingDelivery()
      setIssues([])
      setState('pricing-unavailable')
      return
    }

    setWeeklyOrderProducts(menu.products)
    migrateCartProductIdsForCatalogue(menu.products)
    installPricingDelivery(pricing)
    const nextIssues = validateCartSnapshots(readCartBoxes())
    setIssues(nextIssues)
    setState(nextIssues.length ? 'stale-cart' : 'ready')
  }, [allowHostedSuccessReturn, storeStatus.state])

  useEffect(() => {
    void refreshAuthority()
    const timer = window.setInterval(() => void refreshAuthority(), 15_000)
    const onFocus = () => void refreshAuthority()
    const onVisibility = () => { if (document.visibilityState === 'visible') void refreshAuthority() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [refreshAuthority])

  if (storeStatus.state !== 'open' && !allowHostedSuccessReturn) {
    return (
      <ClosedStoreState
        variant="order"
        mode={storeStatus.state}
        status={storeStatus.status}
      />
    )
  }

  if (state === 'loading') {
    return <main className="menu-authority-gate" role="status" aria-live="polite"><strong>Loading current order details…</strong><span>One moment while we verify this week’s flavours and pricing.</span></main>
  }
  if (state === 'menu-unavailable') {
    return <main className="menu-authority-gate" role="alert"><strong>Menu temporarily unavailable</strong><span>Please refresh in a moment. Your saved order has not been changed.</span></main>
  }
  if (state === 'pricing-unavailable') {
    return <main className="menu-authority-gate" role="alert"><strong>Current pricing temporarily unavailable</strong><span>Please refresh in a moment. We won’t continue with an unverified price.</span></main>
  }
  if (state === 'stale-cart' && target === 'checkout') {
    return <main className="menu-authority-gate"><SavedCartAlert target="checkout" issues={issues} /></main>
  }

  const Page = target === 'cart' ? CartPageV3 : CheckoutPageV3
  return (
    <>
      {state === 'stale-cart' ? <SavedCartAlert target="cart" issues={issues} /> : null}
      <Suspense fallback={null}><Page /></Suspense>
    </>
  )
}
